import { useEffect, useRef } from 'react'
import { isCancelledError, useQuery } from '@tanstack/react-query'
import {
  changeWarehouseStatus,
  getAllWarehouses,
  getWarehouse,
  getWarehouseAdminList,
  toMasterGateError,
  upsertWarehousePolicy,
} from '@/console/api/warehousePolicy'
import { httpStatusOf } from '@/console/api/errors'
import {
  ChangeWarehouseStatusResponse,
  WarehouseDirectory,
  WarehousePolicy,
  WarehousePolicySource,
  WarehousePolicyType,
} from '@/console/api/types'
import { consoleQueryClient } from '@/console/queryClient'
import { PanelState, toPanelState } from '@/console/state/panelState'

/**
 * LOGISPRD-15602 — 창고 탭 조회 훅. `data/holds.ts` 와 같은 패턴이다(`enabled:false` + 명시적 refetch,
 * StrictMode 이중발화는 ref 가드로 막는다).
 */
const HTTP_NOT_FOUND = 404

export const warehousePolicyQueryKeys = {
  all: ['console', 'warehouse-policy'] as const,
  directory: () => [...warehousePolicyQueryKeys.all, 'directory'] as const,
  policies: (code: string | null) => [...warehousePolicyQueryKeys.all, 'policies', code] as const,
}

export type WarehouseDirectoryQuery = { state: PanelState<WarehouseDirectory>; refetch: () => void }
export type WarehousePoliciesQuery = { state: PanelState<WarehousePolicySource>; refetch: () => void }

/**
 * admin 목록을 읽고, 엔드포인트가 아직 배포되지 않아 404 면 활성 창고 목록으로 폴백한다(속성 필드는 null).
 * 그 외 에러는 그대로 던져 화면이 에러로 보여준다.
 */
const fetchDirectory = async (): Promise<WarehouseDirectory> => {
  try {
    const list = await getWarehouseAdminList()
    return { mode: 'admin', lesLookup: list.lesLookup, items: list.warehouses }
  } catch (error) {
    if (httpStatusOf(error) !== HTTP_NOT_FOUND) {
      throw error
    }
    const views = await getAllWarehouses()
    return {
      mode: 'active',
      lesLookup: null,
      items: views.map((v) => ({
        warehouseCode: v.warehouseCode,
        warehouseName: v.warehouseName,
        active: true,
        attributesComplete: null,
        missingAttributes: null,
        lesOperating: null,
      })),
    }
  }
}

export const useWarehouseDirectory = (): WarehouseDirectoryQuery => {
  const query = useQuery({ queryKey: warehousePolicyQueryKeys.directory(), queryFn: fetchDirectory, enabled: false })
  const { refetch } = query
  const hasAutoFetchedRef = useRef(false)

  useEffect(() => {
    if (hasAutoFetchedRef.current) {
      return
    }
    hasAutoFetchedRef.current = true
    refetch().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 시 1회만(가드로 StrictMode 이중발화 방지).
  }, [])

  const base = toPanelState(query, '창고가 없어요.', (directory) => directory.items.length === 0)
  // 목록도 masterGate 뒤에 있다 — 거절은 저장과 같은 권한 문구로 보인다.
  const state: PanelState<WarehouseDirectory> = base.status === 'error' ? { status: 'error', ...toMasterGateError(query.error) } : base

  return {
    state,
    refetch: () => {
      refetch().catch(() => undefined)
    },
  }
}

/** get-warehouse → (null 이면) 활성 목록에서 같은 코드 → 없으면 none. */
const fetchPolicies = async (code: string): Promise<WarehousePolicySource> => {
  const view = await getWarehouse(code)
  if (view) {
    return { source: 'warehouse', view }
  }
  const fromList = (await getAllWarehouses()).find((v) => v.warehouseCode === code)
  return fromList ? { source: 'activeList', view: fromList } : { source: 'none', view: null }
}

/**
 * 선택된 창고의 정책을 조회한다. `code` 가 바뀌면 새 키로 다시 조회하고, null 이면 아무것도 하지 않는다(idle).
 * 마지막으로 조회를 건 code 를 ref 에 둬서 StrictMode 이중 effect 가 같은 code 를 두 번 부르지 않게 한다.
 * source `none` 은 empty 가 아니라 ready 다 — 화면이 잠금 안내를 그린다.
 */
export const useWarehousePolicies = (code: string | null): WarehousePoliciesQuery => {
  const query = useQuery({
    queryKey: warehousePolicyQueryKeys.policies(code),
    queryFn: () => fetchPolicies(code as string),
    enabled: false,
  })
  const { refetch } = query
  const lastFetchedCodeRef = useRef<string | null>(null)

  useEffect(() => {
    if (code === null) {
      lastFetchedCodeRef.current = null
      return
    }
    if (lastFetchedCodeRef.current === code) {
      return
    }
    lastFetchedCodeRef.current = code
    refetch().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- code 변경에만 반응한다. refetch 는 키에 묶여 매 렌더 갱신된다.
  }, [code])

  return {
    state: toPanelState(query, '창고 정보를 찾을 수 없어요.', () => false),
    refetch: () => {
      if (code !== null) {
        refetch().catch(() => undefined)
      }
    },
  }
}

/**
 * 같은 창고를 다시 읽어 캐시(= 카드 초기값)를 서버 값으로 맞춘다. 훅과 같은 키·같은 queryFn 을 써야 캐시 모양이 갈리지 않는다.
 *
 * 재조회 실패는 삼킨다 — upsert 는 이미 반영됐는데 여기서 던지면 화면이 "저장 실패" 를 띄워 담당자가
 * 같은 저장을 다시 누른다(`data/holds.ts` `deleteHoldRule` 과 같은 이유). 갱신이 안 된 화면은
 * "새로고침" 으로 복구할 수 있다.
 */
/** 뒤이은 쓰기가 연달아 취소해도 끝나도록 둔 상한. 넘으면 재조회 실패로 본다. */
const MAX_CANCELLED_REJOINS = 3

/**
 * 진행 중인 같은 키 조회에 합쳐서(없으면 새로) 결과를 기다린다.
 * 뒤이은 같은 창고 쓰기가 이 조회를 취소하면 그 쓰기의 재조회에 다시 합쳐 성공을 확인한다 — 취소만으로 성공이라 하면
 * 그 재조회가 실패했을 때 화면이 편집값을 지운 채 옛 서버 값을 보인다. 취소 시점은 두 upsert 가 모두 끝난 뒤라
 * 다시 합친 조회는 이 저장 이후 값이다.
 */
const awaitPoliciesFetch = (code: string, rejoinsLeft: number): Promise<boolean> =>
  consoleQueryClient
    .fetchQuery({ queryKey: warehousePolicyQueryKeys.policies(code), queryFn: () => fetchPolicies(code) })
    .then(() => true)
    .catch((err: unknown) => (isCancelledError(err) && rejoinsLeft > 0 ? awaitPoliciesFetch(code, rejoinsLeft - 1) : false))

const refetchPoliciesChecked = async (code: string): Promise<boolean> => {
  // 저장 전에 출발한 같은 키 조회가 진행 중이면 fetchQuery 가 그 promise 에 합쳐져 저장 전 값을 받는다 — 먼저 취소한다.
  // (refetchQueries 는 enabled:false 쿼리를 건너뛰어 쓸 수 없다.)
  await consoleQueryClient.cancelQueries({ queryKey: warehousePolicyQueryKeys.policies(code), exact: true })
  return awaitPoliciesFetch(code, MAX_CANCELLED_REJOINS)
}

const refetchPolicies = async (code: string): Promise<void> => {
  await refetchPoliciesChecked(code)
}

/**
 * 활성 전환 등으로 원천이 바뀐 뒤 정책을 다시 읽는다(실패는 삼킨다).
 * 훅의 첫 조회가 아직 진행 중이면 `refetch()` 는 그 조회에 합쳐져 전환 전 원천이 남는다 — 먼저 취소하고 새로 요청한다.
 */
export const refreshWarehousePolicies = (code: string): Promise<void> => refetchPolicies(code)

/** type 1건을 upsert 하고 같은 창고를 다시 읽는다. */
export const saveWarehousePolicy = async (code: string, policy: WarehousePolicy): Promise<void> => {
  await upsertWarehousePolicy({ warehouseCode: code, policy })
  await refetchPolicies(code)
}

export type SavePoliciesResult = {
  saved: WarehousePolicyType[]
  /** 저장 후 재조회가 성공했는지. 저장 0건이면 재조회하지 않으므로 false. */
  refetched: boolean
  failed?: { type: WarehousePolicyType; error: unknown }
}

/**
 * 여러 type 을 배열 순서대로 1건씩 upsert 한다. 첫 실패에서 멈추고 이후는 보내지 않는다.
 * 던지지 않고 결과로 돌려준다 — 일부만 반영된 상태를 화면이 구분해 보여줘야 해서다.
 * 1건이라도 저장됐으면 재조회한다.
 */
export const saveWarehousePolicies = async (code: string, policies: WarehousePolicy[]): Promise<SavePoliciesResult> => {
  const saved: WarehousePolicyType[] = []

  // 재귀로 순차 전송한다(for-of 금지 규칙). 첫 실패에서 failed 를 돌려주며 멈춘다.
  const sendFrom = async (index: number): Promise<SavePoliciesResult['failed']> => {
    if (index >= policies.length) {
      return undefined
    }
    const policy = policies[index]
    try {
      await upsertWarehousePolicy({ warehouseCode: code, policy })
    } catch (error) {
      return { type: policy.type, error }
    }
    saved.push(policy.type)
    return sendFrom(index + 1)
  }

  const failed = await sendFrom(0)

  const refetched = saved.length > 0 ? await refetchPoliciesChecked(code) : false

  return failed ? { saved, refetched, failed } : { saved, refetched }
}

// 재조회하지 않는다 — 화면이 directory.refetch 로 갱신한다.
export const setWarehouseActive = (code: string, active: boolean): Promise<ChangeWarehouseStatusResponse> =>
  changeWarehouseStatus({ warehouseCode: code, active })
